import { Integration } from "@/types/general";
import { FetchServerError, isErrorWithMessage } from "@redux/api/query";

const REIMPORT_CONFIRMATION_ERROR_CODE = "REIMPORT_REQUIRES_CONFIRMATION";
const IMPORT_ENTITIES: Integration.Import.Entity[] = [
    "customer",
    "supplier",
    "product",
    "sale_order",
    "category",
];

/**
 * Checking whether a raw backend entity value belongs to the supported import entity contract.
 */
function isImportEntity(entity: unknown): entity is Integration.Import.Entity {
    return (
        typeof entity === "string" && IMPORT_ENTITIES.includes(entity as Integration.Import.Entity)
    );
}

/**
 * Building an entity-keyed lookup from integration import history so import modals can quickly detect re-imports.
 */
export function getIntegrationImportHistory(
    integration?: Pick<Integration.Components.Integration, "importHistory">
): Integration.Import.HistoryByEntity {
    return (integration?.importHistory ?? []).reduce<Integration.Import.HistoryByEntity>(
        (acc, item) => {
            acc[item.entity] = item;
            return acc;
        },
        {}
    );
}

/**
 * Checking whether a single import history item represents an entity that has already been imported.
 */
export function isPreviouslyImported(historyItem?: Integration.Import.HistoryItem): boolean {
    return historyItem?.imported === true;
}

/**
 * Selecting the planned import entities that have previous successful import history and need confirmation.
 */
export function getRiskyImportEntities(
    plan: Integration.Import.Plan,
    historyByEntity: Integration.Import.HistoryByEntity
): Integration.Import.Entity[] {
    return plan.entities.filter((entity) => isPreviouslyImported(historyByEntity[entity]));
}

/**
 * Selecting the planned import entities that are not part of the confirmation-required entity set.
 */
export function getSafeImportEntities(
    plan: Integration.Import.Plan,
    riskyEntities: Integration.Import.Entity[]
): Integration.Import.Entity[] {
    return plan.entities.filter((entity) => !riskyEntities.includes(entity));
}

/**
 * Detecting the backend conflict response that means the import must be retried with explicit re-import confirmation.
 */
export function isReimportConfirmationError(error: unknown): error is FetchServerError {
    if (!isErrorWithMessage(error) || error.code !== 409) {
        return false;
    }

    const responseData = error.__origin.response?.data as any;

    return (
        responseData?.code === REIMPORT_CONFIRMATION_ERROR_CODE &&
        Array.isArray(responseData.entities) &&
        responseData.entities.length > 0 &&
        responseData.entities.every(isImportEntity)
    );
}

/**
 * Reading the confirmation-required entities from a backend re-import conflict response.
 */
export function getReimportConfirmationErrorEntities(
    error: FetchServerError
): Integration.Import.Entity[] {
    const responseData = error.__origin.response?.data as any;

    return responseData.entities;
}
